import React, { useState, useEffect, useContext, useMemo } from "react";
import { useTable } from "@refinedev/antd";
import { NextActivityCell, useNextActivity } from "@/components/history/NextActivity";
import {
  Input,
  Table,
  Button,
  theme,
  type TableColumnsType,
  Form,
  Grid,
  Flex,
  Typography,
  message,
  Rate,
  Badge,
} from "antd";
import { CrudFilters, HttpError, useNavigation, useGo } from "@refinedev/core";
import { OrdersFilters } from "./ordersFilters";
import {
  ShopOutlined,
  DownloadOutlined,
  TruckOutlined,
} from "@ant-design/icons";
import { LateralModal } from "@/components/modals/LateralModal";
import { PaginationTotal } from "@/components/pagination-total";
import {
  axiosInstance,
  billingDisplayName,
  deriveOrderShipmentStatus,
  ORDER_STATUS_GROUPS,
  filterUrlGenerate,
  formatPrice,
  getSortLabel,
  ShipmentProgress,
  useFormatLastUpdate,
  nombreDeEntrega,
} from "@/utilities";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { utils } from "xlsx";
import {
  API_URL_CONTACTS,
  API_URL_ORDERS,
  API_URL_SELLERS,
  API_URL_STORES,
  LINK_MARKETPLACE_HASH,
} from "@/services/XCONS_ENV";
import { useSelectedStores } from "@/config/context/SelectedItemsContext";
import {
  BoxCloseIcon,
  FilterTags,
  SellerIcon,
  resolveAppliedFilter,
  toCustomersFilterValue,
  toOperatorsFilterValue,
  useAppliedFilterValue,
} from "@/components";
import { useTranslation } from "react-i18next";
import { TFunction, i18n as I18nType } from "i18next";
import { useNavigate } from "react-router-dom";
import StatusTag from "@/components/tags/status-orders";
import { useStoreFilters } from "@/hooks/useStoreFilters";
import { StoreFilter } from "@/components/select-store-filter";
import StatusDeliveryTag from "@/components/tags/status-delivery";
import {
  filterOutOperators,
  useOperatorContacts,
} from "@/hooks/useOperatorContacts";
import { Tooltip } from "antd";
import type { SorterResult, SortOrder } from "antd/es/table/interface";
import { FILAS_POR_PAGINA, OPCIONES_POR_PAGINA } from "@/config/paginacion";

type SortLabels = {
  none: string;
  asc: string;
  desc: string;
};

const { Title, Text, Link } = Typography;
const { Search } = Input;
type BadgeStatusType =
  | "success"
  | "processing"
  | "default"
  | "error"
  | "warning";

export const Orders: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const [storesSelectProps, setStoresSelectProps] = useState<any>();
  const [sellersSelectProps, setSellersSelectProps] = useState<any>();
  const [showAllMap, setShowAllMap] = useState<Record<string, boolean>>({});
  const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
  const [pushContactId, setPushContactId] = useState<string | null>(null);
  const formatLastUpdate = useFormatLastUpdate();
  const screens = Grid.useBreakpoint();
  const { push } = useNavigation();
  const go = useGo();
  const { selectedStores } = useSelectedStores();
  const operatorsAplicados = useAppliedFilterValue("operators");
  const customersAplicados = useAppliedFilterValue("customers");
  const { token } = theme.useToken();
  const { t, i18n }: { t: TFunction; i18n: I18nType } = useTranslation();
  const operatorContactIds = useOperatorContacts();
  const [columnSortOrders, setColumnSortOrders] = useState<
    Record<string, SortOrder | null>
  >({});

  const STATUS_FILTER_MAP: Record<string, string[]> = {
    open: [...ORDER_STATUS_GROUPS.open],
    complete: [...ORDER_STATUS_GROUPS.complete],
    cancel: [...ORDER_STATUS_GROUPS.canceled],
  };

  const onSearch = (params: any) => {
    const filters: CrudFilters = [];
    const {
      main_search,
      sellerCode,
      calendar,
      calendar_created,
      participant_toggle,
      operators,
      customers,
      deliveryStatus,
      deliveryMethod,
      paymentStatus,
      paymentMethod,
      orderStatus,
      filters: existingFilters,
    } = params;

    const mappedStatuses =
      STATUS_FILTER_MAP[orderStatus] ||
      (orderStatus ? [orderStatus] : undefined);

    if (existingFilters) {
      existingFilters.forEach((filter: any) => {
        filters.push(filter);
      });
    }

    // Operadores (por afiliación) y Clientes, en lista (xcons-docs#308). Reemplazan al `customerId`
    // que salía de parsear "Nombre, userId". Con el drawer cerrado se sostiene el último aplicado.
    const op = resolveAppliedFilter({
      field: "operators",
      formValue: operators,
      serialize: toOperatorsFilterValue,
      existingFilters,
      applied: operatorsAplicados,
    });
    if (!op.skip) filters.push({ field: "operators", operator: "eq", value: op.value });
    const cu = resolveAppliedFilter({
      field: "customers",
      formValue: customers,
      serialize: toCustomersFilterValue,
      existingFilters,
      applied: customersAplicados,
    });
    if (!cu.skip) filters.push({ field: "customers", operator: "eq", value: cu.value });

    filters.push(
      {
        field: "search",
        operator: "contains",
        value: main_search,
      },
      {
        field: "user.email",
        operator: "eq",
        value: participant_toggle,
      },
      {
        field: "status",
        operator: "in",
        value: mappedStatuses,
      },
      {
        field: "createdAt",
        operator: "between",
        value: calendar_created
          ? [
            dayjs(calendar_created[0])?.format(),
            dayjs(calendar_created[1]).format(),
          ]
          : undefined,
      },
      {
        field: "updatedAt",
        operator: "between",
        value: calendar
          ? [dayjs(calendar[0])?.format(), dayjs(calendar[1]).format()]
          : undefined,
      },
      {
        field: "sellerCode",
        operator: "in",
        value: sellerCode,
      },
      // {
      //   field: "quoteSellers.total",
      //   operator: "between",
      //   value:
      //     price_product_from !== undefined && price_product_to !== undefined
      //       ? [price_product_from, price_product_to]
      //       : price_product_from !== undefined
      //       ? [price_product_from, Number.MAX_VALUE]
      //       : price_product_to !== undefined
      //       ? [0, price_product_to]
      //       : undefined,
      // },
      {
        field: "shipments.status",
        operator: "in",
        value: deliveryStatus,
      },
      {
        field: "shipments.deliveryMethod",
        operator: "in",
        value: deliveryMethod,
      },
      {
        field: "payments.status",
        operator: "in",
        value: paymentStatus,
      },
      {
        field: "payments.paymentMethod",
        operator: "in",
        value: paymentMethod,
      },
    );
    setFilters(filters);
    return filters;
  };

  const {
    tableQuery: { data, isLoading, isFetching, refetch },
    searchFormProps,
    tableProps,
    filters,
    setFilters,
  } = useTable<HttpError>({
    resource: "orders",
    pagination: {
      pageSize: FILAS_POR_PAGINA,
      mode: "server",
    },
    sorters: {
      mode: "server",
      initial: [
        {
          field: "updatedAt",
          order: "desc",
        },
      ],
    },
    syncWithLocation: true,
    onSearch: onSearch,
  });

  const isLoadingTable = !data || isFetching;

  const total = data?.total || 0;

  const dataSource = useMemo(
    () => (isLoadingTable ? [] : data?.data || []),
    [isLoadingTable, data],
  );

  const getStores = async () => {
    try {
      const response = await axiosInstance.get(API_URL_STORES);
      const stores = response.data.data;
      setStoresSelectProps(stores);
    } catch (error) {
      console.error(error);
    }
  };

  const getSellers = async () => {
    try {
      const response = await axiosInstance.get(API_URL_SELLERS);
      const sellers = response.data.data;
      setSellersSelectProps(sellers);
    } catch (error) {
      console.error(error);
    }
  };

  const getContactId = async (userId: string) => {
    setPushContactId(userId);
    try {
      const response = await axiosInstance.get(
        `${API_URL_CONTACTS}?filter.userId=${userId}`,
      );
      const contactId = response.data.data[0].contactId;
      push(`/contacts/${contactId}`);
    } catch (error) {
      console.error(error);
    } finally {
      setPushContactId(null);
    }
  };

  useEffect(() => {
    getSellers();
    getStores();
  }, []);

  const downloadData = async () => {
    setLoadingDownload(true);
    try {
      const respuesta = await axiosInstance.get(
        `${API_URL_ORDERS}/?page=1&limit=${data?.total}&${filterUrlGenerate(
          filters,
        )}`,
      );

      const allData = respuesta.data.data;

      if (!allData || allData?.length === 0) {
        message.error(t("message.noDataFound"));
        return;
      }

      const columnsToRemove = [
        "status",
        "shareHash",
        "regionId",
        "latitude",
        "longitude",
        "defaultBilling",
        "accessLevel",
      ];

      const filteredData = allData.map((item: any) => {
        const newItem = { ...item };
        columnsToRemove.forEach((column) => {
          delete newItem[column];
        });
        return newItem;
      });

      const documentName = `orders_xcons_${new Date().toISOString()}.xlsx`;
      const worksheet = utils.json_to_sheet(filteredData, {
        header: Object.keys(filteredData[0]),
      });
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, "Xcons data");
      XLSX.writeFile(workbook, documentName);

      message.success(t("message.archiveDownload"));
    } catch (error) {
      message.error(t("message.noDataFound"));
      console.error(error);
    } finally {
      setLoadingDownload(false);
    }
  };

  const toggleShowAll = (id: string) => {
    setShowAllMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const syncHeights = () => {
    const titleElement = document.querySelector(
      ".title-container",
    ) as HTMLElement;
    const contactsElement = document.querySelector(
      ".contacts-container",
    ) as HTMLElement;
    const storesElement = document.querySelector(
      ".store-container",
    ) as HTMLElement;
    const storesPermissionsElement = document.querySelector(
      ".storesPermissions-container",
    ) as HTMLElement;
    const sellerElement = document.querySelector(
      ".seller-container",
    ) as HTMLElement;
    const sellerPermissionsElement = document.querySelector(
      ".sellerPermissions-container",
    ) as HTMLElement;

    if (
      !titleElement ||
      !contactsElement ||
      !storesElement ||
      !storesPermissionsElement ||
      !sellerElement ||
      !sellerPermissionsElement
    )
      return;

    const contactsHeight = contactsElement.offsetHeight;
    const storesHeight = storesPermissionsElement.offsetHeight;
    const sellerHeight = sellerPermissionsElement.offsetHeight;

    titleElement.style.height = `${contactsHeight}px`;
    storesElement.style.height = `${storesHeight}px`;
    sellerElement.style.height = `${sellerHeight}px`;
  };

  useEffect(() => {
    syncHeights();
  }, [showAllMap]);

  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
  const EPS = 0.009;



  const getColumnSortOrder = (field: string): SortOrder | null =>
    columnSortOrders[field] ?? null;

  // Next activity (008 F4): programada futura más próxima por pedido (batch, permisos en el WHERE).
  const nextAct = useNextActivity(
    "order",
    (dataSource as any[]).map((r) => r?.incrementId).filter(Boolean),
  );

  const columns: TableColumnsType<any> = [
    {
      title: () => (
        <Tooltip
          title={getSortLabel(getColumnSortOrder("updatedAt"), {
            none: "Click para ordenar por última actualización",
            asc: "Última actualización (más antigua primero)",
            desc: "Última actualización (más reciente primero)",
          })}
        >
          <span style={{ display: "block" }}>{t("orders.table.status")}</span>
        </Tooltip>
      ),
      dataIndex: "updatedAt",
      key: "updatedAt",
      fixed: "left",
      width: 180,
      sorter: true,
      showSorterTooltip: false,

      render: (_status, record) => {
        const statusMap = {
          open: [
            "invoiced",
            "in_transit",
            "in_shipping",
            "payment_review",
            "pending",
            "pending_payment",
            "processing",
            "preparing",
          ],
          complete: ["complete", "closed"],
          canceled: ["canceled", "devuelto"],
        };

        const getStatusBadgeProps = (
          status: string,
        ): {
          color: BadgeStatusType;
          label: string;
        } => {
          if (statusMap.open.includes(status)) {
            return { color: "default", label: t("quotes.status.Open") };
          }
          if (statusMap.complete.includes(status)) {
            return { color: "success", label: t("orders.status.complete") };
          }
          if (statusMap.canceled.includes(status)) {
            return { color: "error", label: t("orders.status.cancel") };
          }
          return { color: "warning", label: t("quotes.status.Open") };
        };

        let derivedStatus = deriveOrderShipmentStatus(record);

        const badge = getStatusBadgeProps(derivedStatus);

        return (
          <Flex gap={12} vertical>
            <Flex gap={6} align="center">
              <Badge status={badge.color} />
              <Text strong style={{ fontSize: 14 }}>
                {badge.label}
              </Text>
            </Flex>
            <Flex gap={4} vertical>
              <Text
                strong
                style={{ color: token.colorTextSecondary, fontSize: 12 }}
              >
                {t("orders.table.created")}
              </Text>
              <Text style={{ color: token.colorTextSecondary }}>
                {dayjs(record.createdAt).format("DD/MM/YY hh:mm a")}
              </Text>
            </Flex>
            <Flex gap={4} vertical>
              <Text
                strong
                style={{ color: token.colorTextSecondary, fontSize: 12 }}
              >
                {t("quotes.modified")}
              </Text>
              <Text style={{ color: token.colorTextSecondary }}>
                {dayjs(record.updatedAt).format("DD/MM/YY hh:mm a")}
              </Text>
            </Flex>
          </Flex>
        );
      },
    },
    {
      title: () => (
        <Tooltip
          title={getSortLabel(getColumnSortOrder("incrementId"), {
            none: "Click para ordenar por última actualización",
            asc: "Ascendente",
            desc: "Descendente",
          })}
        >
          <span style={{ display: "block" }}>{t("orders.table.order")}</span>
        </Tooltip>
      ),
      dataIndex: "incrementId",
      key: "incrementId",
      fixed: "left",
      width: 200,
      className: "column-vertical-top",
      showSorterTooltip: false,

      sorter: true,
      render: (incrementId, record) => {
        const showAll = showAllMap[incrementId] || false;
        const sellers = record.seller || [];
        const hasMoreSellers = sellers.length > 2;

        return (
          <Flex vertical>
            <Flex
              align="start"
              justify="space-between"
              className="title-container"
              style={{ marginBottom: 25 }}
              gap={8}
            >
              <Typography.Paragraph
                style={{
                  margin: 0,
                  maxWidth: 330,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                ellipsis={{
                  tooltip: `#${incrementId}`,
                }}
              >
                <Text strong style={{ color: token.colorLinkHover }}>
                  {`#${incrementId}`}
                </Text>
              </Typography.Paragraph>

              {hasMoreSellers && (
                <Button
                  shape="round"
                  size="small"
                  onClick={() => toggleShowAll(incrementId)}
                  icon={showAll ? "-" : "+"}
                  iconPosition="end"
                ></Button>
              )}
            </Flex>

            <Flex vertical align="start" gap={4}>
              {/* Mostrar el store */}
              <div className="store-container">
                <Flex gap={4} align="center">
                  <ShopOutlined /> {record.store?.name || "N/A"}
                </Flex>
              </div>

              {/* Mostrar los sellers */}
              <div style={{ width: "80%" }}>
                <Flex style={{ width: "100%" }} justify="space-between">
                  <Flex vertical align="start" gap={8}>
                    <Text style={{ color: token.colorTextSecondary }}>
                      <SellerIcon /> {sellers.name}
                    </Text>
                  </Flex>
                </Flex>
              </div>
            </Flex>
          </Flex>
        );
      },
    },
    {
      title: t("menu.label.contacts"),
      className: "column-vertical-top",
      dataIndex: "participations",
      fixed: "left",
      width: 200,
      render: (participations, record) => {
        const showAll = showAllMap[record.id] || false;
        const whiteSpace = <span style={{ color: "transparent" }}>_</span>;

        const classifyAndSortParticipants = (participants: any[]) => {
          const visible = filterOutOperators(participants, operatorContactIds);
          const usersWithoutPermissions = visible.filter(
            (p) => !p.participationType || p.participationType === "",
          );

          // Owner = position 0 (or lowest position as fallback). Sorting by
          // position ascending puts the owner first, so the default slice(0,1)
          // in the column always renders the owner instead of whichever
          // participation happened to sort first alphabetically.
          const byPositionAsc = (a: any, b: any) =>
            (Number(a.position) || 0) - (Number(b.position) || 0);

          const usersWithStorePermissions = visible
            .filter((p) => p.participationType === "store")
            .sort(byPositionAsc);

          const usersWithSellerPermissions = visible
            .filter((p) => p.participationType === "seller")
            .sort(byPositionAsc);

          return {
            usersWithoutPermissions,
            usersWithStorePermissions,
            usersWithSellerPermissions,
          };
        };

        const {
          usersWithoutPermissions,
          usersWithStorePermissions,
          usersWithSellerPermissions,
        } = classifyAndSortParticipants(participations || []);

        return (
          // 200px de columna (Diseño, 2026-10-19): la tabla agranda una columna si el
          // contenido no entra, así que el ancho se fija acá y los nombres largos se cortan
          // con "…" (`.celda-contactos` en config.css).
          <Flex vertical className="celda-contactos">
            {/* Usuarios sin permisos */}
            <div className="contacts-container" style={{ marginBottom: 25 }}>
              <Flex justify="space-between" align="start" gap={10}>
                <Button
                  style={{ padding: 0, height: "auto" }}
                  loading={pushContactId === record?.user?.userId}
                  type="link"
                  onClick={(e) => {
                    e.stopPropagation();
                    getContactId(record?.user?.userId);
                  }}
                >
                  <Text
                    strong
                    key={record?.user?.userId}
                    style={{ color: token.colorLinkHover }}
                  >
                    {record?.customerFirstname} {record?.customerLastname}
                  </Text>
                </Button>
              </Flex>
            </div>

            {/* Usuarios sin permisos */}
            {/* <div className="contacts-container" style={{ marginBottom: 25 }}>
              <Flex justify="space-between" align="start" gap={10}>
                {usersWithoutPermissions.length > 0 ? (
                  <Flex vertical align="start" gap={8}>
                    {usersWithoutPermissions
                      .slice(0, showAll ? usersWithoutPermissions.length : 1)
                      .map((user: any, index: number) => (
                        <Text
                          strong
                          key={`noperm-${user.contactId}-${index}`}
                          style={{ color: token.colorLinkHover }}
                        >
                          <Link href={`/contacts/${user?.contactId}`}>
                            {user.firstname} {user.lastname}
                          </Link>
                        </Text>
                      ))}
                  </Flex>
                ) : (
                  <Text style={{ color: token.colorTextSecondary }}>
                    {whiteSpace}
                  </Text>
                )}
              </Flex>
            </div> */}

            {/* Permisos en tiendas */}
            <div className="storesPermissions-container">
              {usersWithStorePermissions.length > 0 ? (
                <Flex vertical align="start">
                  {usersWithStorePermissions
                    .slice(0, showAll ? usersWithStorePermissions.length : 1)
                    .map((user: any, index: number) => (
                      <Text
                        key={`storeperm-${user.contact?.contactId}-${index}`}
                        strong={index !== 0}
                        style={{ color: token.colorText }}
                      >
                        {user.contact?.user?.firstname}{" "}
                        {user.contact?.user?.lastname}
                      </Text>
                    ))}
                </Flex>
              ) : (
                <Text style={{ color: token.colorTextSecondary }}>
                  {whiteSpace}
                </Text>
              )}
            </div>

            {/* Permisos en sellers */}
            <div className="sellerPermissions-container">
              {usersWithSellerPermissions.length > 0 ? (
                <Flex vertical align="start">
                  {usersWithSellerPermissions
                    .slice(0, showAll ? usersWithSellerPermissions.length : 1)
                    .map((user: any, index: number) => (
                      <Text
                        key={`sellerperm-${user.contact?.contactId}-${index}`}
                        style={{
                          marginBottom: 4,
                          color: token.colorTextSecondary,
                        }}
                      >
                        {user.contact?.user?.firstname}{" "}
                        {user.contact?.user?.lastname}
                      </Text>
                    ))}
                </Flex>
              ) : (
                <Text style={{ color: token.colorTextSecondary }}>
                  {whiteSpace}
                </Text>
              )}
            </div>
          </Flex>
        );
      },
    },
    {
      title: () => (
        <Tooltip
          title={getSortLabel(getColumnSortOrder("grandTotal"), {
            none: "Click para ordenar por última actualización",
            asc: "Ascendente",
            desc: "Descendente",
          })}
        >
          <span style={{ display: "block" }}>{t("orders.table.total")}</span>
        </Tooltip>
      ),
      dataIndex: "grandTotal",
      key: "grandTotal",
      sorter: true,
      className: "column-vertical-top",
      width: 200,
      showSorterTooltip: false,

      render: (subtotalInclTax, record) => {
        const grandTotal = record?.grandTotal;

        let balance =
          record?.status === "canceled"
            ? 0
            : record?.payments?.[0]?.status === "invoiced"
              ? 0
              : grandTotal;
        if (Math.abs(balance) < EPS) balance = 0;
        balance = round2(balance);
        return (
          <Flex vertical>
            <Flex vertical align="end" justify="end" style={{ marginTop: 12 }}>
              <Text
                strong
                style={{
                  color: token.colorTextSecondary,
                  fontSize: 12,
                }}
              >
                {t("orders.table.order")}
              </Text>
              <Text
                strong
                style={{ color: token.colorTextSecondary, fontSize: 20 }}
              >
                {formatPrice(record?.grandTotal)}
              </Text>
            </Flex>

            <Flex vertical align="end" justify="end" style={{ marginTop: 12 }}>
              <Text
                strong
                style={{
                  color: token.colorTextSecondary,
                  fontSize: 12,
                }}
              >
                Balance
              </Text>
              <Text style={{ color: token.colorTextSecondary, fontSize: 18 }}>
                {balance === 0 ? `-${formatPrice(record?.grandTotal)}` : `${formatPrice(record?.grandTotal)}`}
              </Text>
            </Flex>
          </Flex>
        );
      },
    },
    {
      title: t("orders.table.payment"),
      dataIndex: "payments",
      width: 270,
      render: (payments: any[], record: any) => {
        const paymentOptions = [
          {
            label: t("paymentmethod.dropdown.banktransfer"),
            value: "banktransfer",
          },
          { label: t("paymentmethod.dropdown.echeq"), value: "checkmo" },
          { label: t("paymentmethod.dropdown.card"), value: "firstdata" },
          {
            label: t("paymentmethod.dropdown.mercadopago"),
            value: "mercadopago_basic",
          },
          { label: t("paymentmethod.dropdown.incash"), value: "purchaseorder" },
        ];

        const paymentOptionsMap = paymentOptions.reduce((acc, option) => {
          acc[option.value] = option.label;
          return acc;
        }, {} as Record<string, string>);

        const getPaymentMethodLabel = (value: string): string => {
          return paymentOptionsMap[value] || "-";
        };

        return (
          <Flex vertical>
            {payments?.map((payment: any, index: number) => {
              // El número de factura vive en invoices[0] (factura subida) con
              // fallback a payment.metadata (valores tipeados aún sin subir).
              const primaryInvoice = payment?.invoices?.[0];
              const invoicePointOfSale =
                primaryInvoice?.pointOfSale ??
                payment?.metadata?.invoicePointOfSale ??
                "";
              const invoiceNumber =
                primaryInvoice?.number ??
                payment?.metadata?.invoiceNumber ??
                "";
              const invoiceValue = [invoicePointOfSale, invoiceNumber]
                .filter(Boolean)
                .join(" - ");
              return (
                <Flex vertical key={index}>
                  <Flex gap={4}>
                    <Text
                      strong
                      style={{ color: token.colorTextSecondary }}
                    >
                      {t("orders.label.invoiceNumberShort")}
                    </Text>
                    {payment?.status === "invoiced" ? (
                      <Text type="secondary">{invoiceValue || "-"}</Text>
                    ) : (
                      <Text style={{ color: "#FF8F1F" }} italic>
                        {record?.payments?.[0]?.status === "canceled"
                          ? "-"
                          : t("orders-label.pending")}
                      </Text>
                    )}
                  </Flex>
                  {payment?.status === "invoiced" && (
                    <Text type="secondary">
                      {`${billingDisplayName(record?.billingAddress) || "-"} ${
                        record?.billingAddress?.vatId || "-"
                      }`}
                    </Text>
                  )}

                  <Text type="secondary" strong style={{ marginTop: 12 }}>
                    {`${record?.billingAddress?.street}`}
                  </Text>

                  <Text strong style={{ marginTop: 12 }}>
                    {payment.metadata?.method_title}
                  </Text>

                </Flex>
              );
            })}
          </Flex>
        );
      },
    },
    {
      title: t("orders.table.paymentStatus"),
      dataIndex: "payments",
      width: 290,
      render: (payments: any[], record: any) => {
        return (
          <Flex vertical gap={8}>
            {payments.map((payment, index) => (
              <Flex
                key={index}
                gap={12}
                align="baseline"
                justify="space-between"
              >
                <Text
                  strong
                  style={{ color: token.colorTextSecondary, minWidth: 130 }}
                >
                  {t("orders.steps.orderInvoice")}
                </Text>
                <StatusTag status={payment.status} />
              </Flex>
            ))}

            {/* Credit notes (status) */}
            {Array.isArray(record?.creditMemos) &&
              record.creditMemos.length > 0 && (
                <Flex vertical gap={6} style={{ marginTop: 6 }}>
                  {record.creditMemos.map((cm: any, i: number) => {
                    return (
                      <Flex
                        key={cm?.creditMemoId}
                        align="baseline"
                        justify="space-between"
                        gap={12}
                      >
                        <Text
                          strong
                          style={{ color: token.colorTextSecondary }}
                        >
                          {t("orders.label.creditnote")}{" "}
                          {record.creditMemos.length > 1 ? i + 1 : ""}
                        </Text>
                        <StatusTag status={cm?.status} />
                      </Flex>
                    );
                  })}
                </Flex>
              )}
          </Flex>
        );
      },
    },
    {
      title: t("orders.table.deliveryStatus"),
      dataIndex: "shippingAddress",
      width: 300,
      render: (shippingAddress, record) => {
        return (
          <Flex vertical gap={4}>
            {record.shipments?.map((shipment: any, index: number) => (
              <Flex
                gap={6}
                align="center"
                justify="start"
                key={shipment.shipmentId || index}
              >
                {shipment.metadata?.cancelationType === "partial" ? (
                  // Cancelación parcial: misma estructura que el resto
                  // (n# + camión) para que quede alineado, con el label
                  // "Partial cancellation delivery n#".
                  <>
                    {shipment.metadata?.deliveryGroup ?? index + 1}
                    <TruckOutlined />
                    <Text
                      strong
                      style={{
                        color: token.colorText,
                        maxWidth: 280,
                        display: "inline-block",
                        verticalAlign: "bottom",
                      }}
                    >
                      {t(
                        "orders.label.creditnoteDeliveryPartialCancellation",
                        {
                          number:
                            shipment.metadata?.deliveryGroup ?? index + 1,
                        },
                      )}
                    </Text>
                  </>
                ) : (
                  <>
                    {shipment.metadata?.name ? index + 1 : ""}
                    {shipment.metadata?.name ? <TruckOutlined /> : null}
                    <Text
                      strong
                      ellipsis={{ tooltip: nombreDeEntrega(shipment.metadata?.name) }}
                      style={{
                        color: token.colorText,
                        maxWidth: 280,
                        display: "inline-block",
                        verticalAlign: "bottom",
                      }}
                    >
                      {nombreDeEntrega(shipment.metadata?.name) || "Cancelación parcial"}
                    </Text>
                  </>
                )}
                <StatusDeliveryTag status={shipment.status} />
              </Flex>
            ))}
          </Flex>
        );
      },
    },
    {
      title: t("orders.table.delivery"),
      dataIndex: "shippingAddress",
      width: 300,
      render: (shippingAddress, record) => {
        return (
          <Flex vertical>
            <Text strong style={{ color: token.colorText }}>
              <ShipmentProgress shipments={record?.shipments || []} />
            </Text>
            <Text>{record.shippingAddress?.street || ""}</Text>
            <Text>
              {`${record.shippingAddress?.region} ${record.shippingAddress?.city}` ||
                ""}
            </Text>
          </Flex>
        );
      },
    },
    {
      title: t("orders.table.rating"),
      dataIndex: "rating",
      width: 200,
      render: (review, record) => {
        return (
          <Flex vertical>
            {review ? (
              <Rate disabled defaultValue={3} />
            ) : (
              <Text type="secondary">{t("orders.noRatingYet")}</Text>
            )}
          </Flex>
        );
      },
    },
    {
      title: t("orders.table.quote"),
      dataIndex: "quote",
      width: 180,
      render: (quote, record) => {
        return (
          // 180px de columna (Diseño, 2026-10-19): el ancho se fija en el contenido —la
          // tabla agranda la columna si no entra— y un nombre largo se corta con "…".
          <Flex style={{ width: 164, maxWidth: 164 }}>
            {quote?.id ? (
              <Link
                href={`${record?.store?.link}${LINK_MARKETPLACE_HASH}${record?.quote?.shareHash}`}
                target="_blank"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Flex vertical>
                  <Text
                    strong
                    style={{
                      color: token.colorPrimary,
                    }}
                  >
                    #{quote?.id}
                  </Text>
                  <Text
                    strong
                    ellipsis={{ tooltip: quote?.name }}
                    style={{
                      color: token.colorPrimary,
                      maxWidth: 164,
                    }}
                  >
                    {quote?.name}
                  </Text>
                </Flex>
              </Link>
            ) : (
              <Text type="secondary">-</Text>
            )}
          </Flex>
        );
      },
    },
    {
      title: "Próxima actividad",
      key: "nextActivity",
      width: 140,
      render: (_: unknown, record: any) => (
        <NextActivityCell value={nextAct[String(record?.incrementId)]} />
      ),
    },
  ];

  interface HeaderRowProps {
    total?: number;
  }

  const HeaderRow = React.memo(({ total = 0 }: HeaderRowProps) => (
    <div>
      <Flex flex="block" gap="large" wrap={screens.sm ? false : true}>
        <Form {...searchFormProps} style={{ width: "100%" }}>
          <Form.Item name="main_search">
            <Search
              disabled={isFetching}
              placeholder={t("orders.Search.order.quote.and.contact")}
              loading={isLoading}
              allowClear
              onSearch={(value) => {
                if (value.trim() === "") {
                  searchFormProps.form?.setFieldsValue({
                    main_search: undefined,
                  });
                }
                searchFormProps.form?.submit();
              }}
            />
          </Form.Item>
        </Form>

        <Flex
          gap="large"
          justify="center"
          style={{ width: screens.sm ? "auto" : "100%" }}
        >
          <LateralModal
            disabled={isFetching}
            buttonName={t("buttons.filters")}
            title={t("buttons.filters")}
          >
            <OrdersFilters
              searchFormProps={searchFormProps}
              filters={filters}
              storesSelectProps={storesSelectProps}
              selectedStores={selectedStores}
              sellersSelectProps={sellersSelectProps}
              t={t}
            />
          </LateralModal>

          <Button
            loading={loadingDownload}
            onClick={() => downloadData()}
            icon={<DownloadOutlined />}
            iconPosition="end"
          >
            {t("actions.export")}
          </Button>
        </Flex>
      </Flex>

      <FilterTags
        searchFormProps={searchFormProps}
        onSearch={onSearch}
        i18n={i18n}
        filters={filters}
        setFilters={setFilters}
        t={t}
      />

      <Flex justify={total > 0 ? "space-between" : "end"}>
        {total > 0 && (
          <Text style={{ color: token.colorTextSecondary }}>
            {total} {t("orders.table.ordersEntity")}
          </Text>
        )}
      </Flex>
    </div>
  ));

  useStoreFilters({
    setFilters,
    field: "storeCode",
    operator: "in",
  });

  const navigate = useNavigate();

  const handleRowClick = (record: any) => {
    navigate(`/orders/${record?.orderId}`, {
      state: { record: record.orderId },
    });
  };

  return (
    <>
      <Flex gap={32} align="center" style={{ marginBottom: 24 }}>
        <Flex align="center" gap={8}>
          <BoxCloseIcon style={{ transform: "scale(1.4)", height: 24 }} />
          <Title level={3} style={{ marginBottom: 0 }}>
            {t("orders.title")}
          </Title>
        </Flex>
        <StoreFilter stores={storesSelectProps} />
      </Flex>

      <Table
        {...tableProps}
        className="tabla-pedidos"
        style={{ cursor: "pointer" }}
        scroll={{ x: "max-content", y: 55 * 10.5 }}
        rowKey="orderId"
        title={() => <HeaderRow total={total} />}
        components={{ header: { cell: (props: any) => <th {...props} style={{ ...props.style, textAlign: "left", height: 60, padding: "0 8px", verticalAlign: "middle" }} /> } }}
        onRow={(record) => ({
          onClick: () => {
            handleRowClick(record);
          },
        })}
        dataSource={dataSource}
        columns={columns}
        loading={isLoadingTable}
        locale={{
          triggerAsc: t("table.triggerAsc"),
          triggerDesc: t("table.triggerDesc"),
          cancelSort: t("table.cancelSort"),
        }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          pageSizeOptions: OPCIONES_POR_PAGINA,
          locale: {
            items_per_page: t("table.itemsPerPage"),
            prev_page: t("table.prevPage"),
            next_page: t("table.nextPage"),
            jump_to: t("table.jumpTo"),
            jump_to_confirm: t("table.jumpToConfirm"),
            page: t("table.page"),
            next_5: t("table.next5"),
            prev_5: t("table.prev5"),
          },
          showTotal: (total) => {
            return (
              <PaginationTotal total={total} entityName={t("orders.title")} />
            );
          },
        }}
        onChange={(pagination, filters, sorter, extra) => {
          const handleSorter = (s: SorterResult<any>) => {
            const field = s.columnKey || s.field;
            if (!field) return;
            setColumnSortOrders((prev) => ({
              ...prev,
              [String(field)]: s.order ?? null,
            }));
          };
          if (Array.isArray(sorter)) {
            sorter.forEach(handleSorter);
          } else if (sorter) {
            handleSorter(sorter as SorterResult<any>);
          }
          tableProps.onChange?.(pagination, filters, sorter, extra);
        }}
      />

      {children}
    </>
  );
};
